import { useEffect, useRef } from 'react';

// Standardized cadences. Both list and detail pages refresh on the same
// 5s tick so changes from another tab / another user appear quickly.
// The original 30s/15s split felt sluggish during testing — list pages
// don't actually carry much load (page-keyed cache + small payloads), so
// matching detail-page speed is the better default. Setup Mode keeps its
// own faster 3s/15s cadence in useSetupModePolling — that hook stays
// dedicated to scan-in feedback.
export const POLL_INTERVAL_LIST_MS = 5_000;
export const POLL_INTERVAL_DETAIL_MS = 5_000;

interface UsePagePollingOptions {
  // Set to false to suspend polling without unmounting the consumer (useful
  // when an item isn't selected yet, or when a modal is open and the page
  // shouldn't refetch).
  enabled?: boolean;
}

/**
 * Visibility-aware background polling. Calls `refetch` on a fixed interval
 * while the tab is visible; pauses when the tab is hidden; force-refetches
 * once the moment the tab becomes visible again so the user doesn't wait
 * a full interval to see fresh data.
 *
 * `refetch` may optionally accept an AbortSignal. When the next tick fires
 * (or the component unmounts), the previous in-flight call is aborted —
 * callers that propagate the signal to fetch/axios get free request
 * cancellation; callers that ignore it still work correctly.
 */
export function usePagePolling(
  refetch: (signal?: AbortSignal) => unknown | Promise<unknown>,
  intervalMs: number,
  options?: UsePagePollingOptions,
): void {
  const enabled = options?.enabled ?? true;
  // Keep the latest refetch in a ref so consumers passing inline arrow
  // functions don't re-subscribe the effect on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let inflight: AbortController | null = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      inflight?.abort();
      const controller = new AbortController();
      inflight = controller;
      Promise.resolve(refetchRef.current(controller.signal)).catch(() => {
        // Errors during background poll are the consumer's responsibility
        // to surface (or swallow). The polling loop must not die on them.
      });
    };

    const start = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Force one immediate refresh on return so stale data clears
        // without waiting an entire interval.
        tick();
        start();
      } else {
        stop();
      }
    };

    // Mobile Safari + standalone PWAs don't always fire visibilitychange
    // when switching apps or returning from a lock screen — they fire
    // window 'focus' or 'pageshow' (bfcache restore) instead. Listening
    // to all three keeps the "refresh on return" semantics consistent
    // across desktop tabs, mobile browsers, and installed PWAs.
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };

    if (document.visibilityState === 'visible') {
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
      inflight?.abort();
    };
  }, [intervalMs, enabled]);
}
